import { addDays, format, parseISO, startOfWeek } from 'date-fns';
import {
    CalendarDays,
    Columns3,
    HeartPulse,
    Tag,
    ToggleRight,
    UserRound,
} from 'lucide-react';
import { z } from 'zod';

import { ACCOUNT_HEALTH_STATUS_CONFIG } from '@apps/web/components/admin/accounts/account_health_status';
import {
    type FilterChip,
    type FilterDimension,
    type FilterOption,
} from '@apps/web/components/ui/filter_bar';
import {
    ACCOUNT_FEATURES,
    activatedFeatureKeys,
} from '@apps/web/routes/_app/admin/accounts/account_features';
import {
    type AccountKanbanCardRead,
    type AccountKanbanColumnRead,
    type AccountOwnerCandidateRead,
} from '@libraries/typescript/api_client';

// The standard account filter schema shared by every account tab (board,
// account management, calendar, table). Each dimension is a categorical
// select rendered by the shared admin FilterBar; matching happens client-side
// over the fetched kanban cards via cardMatchesFilterChips.

const ACCOUNT_FILTER_DIMENSION_KEYS = [
    'health',
    'stage',
    'kickoff_week',
    'owner',
    'tag',
    'feature',
] as const;
export type AccountFilterDimensionKey =
    (typeof ACCOUNT_FILTER_DIMENSION_KEYS)[number];
export type AccountFilterChip = FilterChip<AccountFilterDimensionKey>;
export type AccountFilterDimension = FilterDimension<AccountFilterDimensionKey>;

// Sentinel option value for cards without an owner. Safe because real owner
// values are email addresses.
export const UNASSIGNED_OWNER_FILTER_VALUE = 'unassigned';

const FILTER_OPERATORS = ['is', 'is_not'] as const;
export type AccountFilterOperator = (typeof FILTER_OPERATORS)[number];

/**
 * Account dimensions only offer is/is-not, but the shared FilterBar chip
 * type allows every operator; collapse anything unexpected to `is`.
 */
export function accountChipOperator(
    operator: AccountFilterChip['operator'] | null,
): AccountFilterOperator {
    return operator === 'is_not' ? 'is_not' : 'is';
}

const storedChipSchema = z.object({
    dimension: z.enum(ACCOUNT_FILTER_DIMENSION_KEYS),
    operator: z.enum(FILTER_OPERATORS).optional(),
    values: z.array(z.string()).min(1),
});

/** Parses chips persisted to localStorage, dropping anything malformed. */
export function parseStoredAccountFilterChips(
    value: string | null,
): Array<AccountFilterChip> {
    if (!value) {
        return [];
    }
    try {
        const parsed = z.array(storedChipSchema).safeParse(JSON.parse(value));
        return parsed.success ? parsed.data : [];
    } catch {
        return [];
    }
}

/**
 * The values a card exposes for one filter dimension. Owner emails are
 * normalized to lowercase so chip values and card values always compare
 * case-insensitively.
 */
function cardDimensionValues(
    card: AccountKanbanCardRead,
    dimension: AccountFilterDimensionKey,
): Array<string> {
    switch (dimension) {
        case 'health':
            return [card.health_status];
        case 'stage':
            return [card.kanban_stage];
        case 'kickoff_week': {
            const weekKey = kickoffWeekKey(card.go_live_date);
            return weekKey === null ? [] : [weekKey];
        }
        case 'owner':
            return [
                card.owner_email?.toLowerCase() ??
                    UNASSIGNED_OWNER_FILTER_VALUE,
            ];
        case 'tag':
            return card.tags.map((tag) => tag.slug);
        case 'feature':
            return activatedFeatureKeys(card.feature_activations ?? []);
        default: {
            const unreachable: never = dimension;
            throw new Error(`Unknown filter dimension: ${String(unreachable)}`);
        }
    }
}

/** Returns the Monday key for a signed kickoff date. */
export function kickoffWeekKey(
    goLiveDate: string | null | undefined,
): string | null {
    if (!goLiveDate) {
        return null;
    }

    return format(
        startOfWeek(parseISO(goLiveDate), { weekStartsOn: 1 }),
        'yyyy-MM-dd',
    );
}

/**
 * Formats a Monday kickoff-week key for filter and group labels. Production
 * kickoff dates span multiple years, so weeks outside the current year carry
 * an explicit year — otherwise a chronological list reads as unsorted at the
 * December → January boundary.
 */
export function kickoffWeekLabel(
    weekKey: string,
    referenceDate: Date = new Date(),
): string {
    const weekStart = parseISO(weekKey);
    const range = `${format(weekStart, 'MMM d')} – ${format(
        addDays(weekStart, 6),
        'MMM d',
    )}`;
    return weekStart.getFullYear() === referenceDate.getFullYear()
        ? range
        : `${range}, ${format(weekStart, 'yyyy')}`;
}

/** A card matches when every chip matches (chips AND together). */
export function cardMatchesFilterChips(
    card: AccountKanbanCardRead,
    chips: ReadonlyArray<AccountFilterChip>,
): boolean {
    return chips.every((chip) => {
        const cardValues = cardDimensionValues(card, chip.dimension);
        const hasMatch = cardValues.some((value) =>
            chip.values.includes(value),
        );
        return chip.operator === 'is_not' ? !hasMatch : hasMatch;
    });
}

function countCardValues(
    cards: ReadonlyArray<AccountKanbanCardRead>,
    dimension: AccountFilterDimensionKey,
): Map<string, number> {
    const counts = new Map<string, number>();
    for (const card of cards) {
        for (const value of new Set(cardDimensionValues(card, dimension))) {
            counts.set(value, (counts.get(value) ?? 0) + 1);
        }
    }
    return counts;
}

function buildOwnerOptions(
    cards: ReadonlyArray<AccountKanbanCardRead>,
    ownerCandidates: ReadonlyArray<AccountOwnerCandidateRead>,
): Array<FilterOption> {
    const counts = countCardValues(cards, 'owner');
    const options: Array<FilterOption> = [
        {
            value: UNASSIGNED_OWNER_FILTER_VALUE,
            label: 'Unassigned',
            count: counts.get(UNASSIGNED_OWNER_FILTER_VALUE) ?? 0,
        },
        ...ownerCandidates.map((owner) => ({
            value: owner.email.toLowerCase(),
            label: owner.name,
            count: counts.get(owner.email.toLowerCase()) ?? 0,
        })),
    ];
    // Owners still on cards but no longer assignable (e.g. departed
    // teammates) must stay filterable.
    const knownValues = new Set(options.map((option) => option.value));
    const extras = new Map<string, string>();
    for (const card of cards) {
        const email = card.owner_email?.toLowerCase();
        if (email && !knownValues.has(email)) {
            extras.set(email, card.owner_name ?? email);
        }
    }
    const extraOptions = [...extras.entries()]
        .map(([value, label]) => ({
            value,
            label,
            count: counts.get(value) ?? 0,
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
    return [...options, ...extraOptions];
}

function buildTagOptions(
    cards: ReadonlyArray<AccountKanbanCardRead>,
): Array<FilterOption> {
    const counts = countCardValues(cards, 'tag');
    const labelsBySlug = new Map<string, string>();
    for (const card of cards) {
        for (const tag of card.tags) {
            labelsBySlug.set(tag.slug, tag.label);
        }
    }
    return [...labelsBySlug.entries()]
        .map(([value, label]) => ({
            value,
            label,
            count: counts.get(value) ?? 0,
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
}

/**
 * Builds the standard filter dimensions from the fetched kanban data. Option
 * counts reflect all fetched cards (before chip filtering) so admins can see
 * how many accounts each choice covers.
 */
export function buildAccountFilterDimensions(
    columns: ReadonlyArray<AccountKanbanColumnRead>,
    ownerCandidates: ReadonlyArray<AccountOwnerCandidateRead>,
): Array<AccountFilterDimension> {
    const cards = columns.flatMap((column) => column.cards);
    const healthCounts = countCardValues(cards, 'health');
    const healthOptions = Object.entries(ACCOUNT_HEALTH_STATUS_CONFIG)
        .sort(([, left], [, right]) => left.sortRank - right.sortRank)
        .map(([value, config]) => ({
            value,
            label: config.label,
            count: healthCounts.get(value) ?? 0,
        }));
    const stageOptions = columns.map((column) => ({
        value: column.stage,
        label: column.title,
        count: column.cards.length,
    }));
    const featureCounts = countCardValues(cards, 'feature');
    // Newest cohorts first: recent kickoff weeks are the ones being worked,
    // and old ones would otherwise bury them at the top of the list.
    const kickoffWeekOptions = [...countCardValues(cards, 'kickoff_week')]
        .sort(([leftKey], [rightKey]) => rightKey.localeCompare(leftKey))
        .map(([value, count]) => ({
            value,
            label: kickoffWeekLabel(value),
            count,
        }));
    return [
        {
            key: 'health',
            label: 'Health',
            operators: [...FILTER_OPERATORS],
            icon: HeartPulse,
            options: healthOptions,
            multiSelect: true,
            searchable: true,
        },
        {
            key: 'stage',
            label: 'Stage',
            operators: [...FILTER_OPERATORS],
            icon: Columns3,
            options: stageOptions,
            multiSelect: true,
            searchable: true,
        },
        {
            key: 'kickoff_week',
            label: 'Kickoff week',
            operators: [...FILTER_OPERATORS],
            icon: CalendarDays,
            options: kickoffWeekOptions,
            multiSelect: true,
            searchable: true,
        },
        {
            key: 'owner',
            label: 'Owner',
            operators: [...FILTER_OPERATORS],
            icon: UserRound,
            options: buildOwnerOptions(cards, ownerCandidates),
            multiSelect: true,
            searchable: true,
        },
        {
            key: 'tag',
            label: 'Tag',
            operators: [...FILTER_OPERATORS],
            icon: Tag,
            options: buildTagOptions(cards),
            multiSelect: true,
            searchable: true,
        },
        {
            key: 'feature',
            label: 'Feature',
            operators: [...FILTER_OPERATORS],
            icon: ToggleRight,
            options: ACCOUNT_FEATURES.map((feature) => ({
                value: feature.key,
                label: feature.label,
                count: featureCounts.get(feature.key) ?? 0,
            })),
            multiSelect: true,
            searchable: true,
        },
    ];
}

/**
 * True for an owner chip that scopes *to* a specific owner (operator `is`), as
 * opposed to an `is_not` exclusion chip. Callers that treat "an owner is
 * selected" as a positive scope (My View, the Needs Review self-scope override)
 * must use this rather than a blanket `dimension === 'owner'` check, since an
 * exclusion chip does not narrow to a single owner.
 */
export function isOwnerIsChip(chip: AccountFilterChip): boolean {
    return chip.dimension === 'owner' && chip.operator !== 'is_not';
}

/** True when the chips scope to exactly the signed-in admin's accounts. */
export function isMyViewActive(
    chips: ReadonlyArray<AccountFilterChip>,
    myEmail: string,
): boolean {
    const email = myEmail.toLowerCase();
    return chips.some(
        (chip) =>
            isOwnerIsChip(chip) &&
            chip.values.length === 1 &&
            chip.values[0] === email,
    );
}

/**
 * Toggles the "My View" shortcut. Activating replaces any owner "is" chip so
 * My View always means exactly "owned by me"; deactivating removes it.
 */
export function toggleMyView(
    chips: ReadonlyArray<AccountFilterChip>,
    myEmail: string,
): Array<AccountFilterChip> {
    const otherChips = chips.filter((chip) => !isOwnerIsChip(chip));
    if (isMyViewActive(chips, myEmail)) {
        return otherChips;
    }
    return [
        ...otherChips,
        {
            dimension: 'owner',
            operator: 'is',
            values: [myEmail.toLowerCase()],
        },
    ];
}
